/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import type React from 'react';
import { createContext, useCallback, useState } from 'react';
import { useMqttV3SubscriptionCallb } from 'utilities/mqtt3/MqttConnection';
import MqttStatus from 'utilities/mqtt3/MqttStatus';
import PubSubTopics from 'utilities/PubSub/PubSubTopics';
import type { DataFrame, MetadataFrame } from 'utilities/PubSub/PubSubTypes';
import { createPubSubAddressSpaceStore, type PubSubAddressSpace } from './PubSubAddressSpace';
import { createPubSubMesageHandlingStore, PubSubMesageHandling } from './PubSubMessageHandling';

interface IOpcUaMqttPubSubProps {
  topicPath: string;
}

export interface OpcUaMqttPubSubContextType {
  addrSpaceStore: PubSubAddressSpace;
  msgHandlingStore: PubSubMesageHandling;
}

export const OpcUaMqttPubSubContext = createContext<OpcUaMqttPubSubContextType | undefined>(undefined);
/// version of the stored address space
/// this is used to detect changes in the address space
export const OpcUaMqttPubSubContextVersion = createContext<number>(0);

var debug = 0;

function _parseBuffer<T>(buffer: Buffer): T | undefined {
  const data = JSON.parse(buffer.toString('utf-8')) as T;
  return data;
}

const showPubSubDebug = false;

export default function OpcUaMqttPubSub({ topicPath, children }: React.PropsWithChildren<IOpcUaMqttPubSubProps>) {
  const log: string[] = [];
  log.push(`OpcUaMqttPubSub- ${++debug}`);

  interface ITopicStatistic {
    lastData: Buffer;
    numReceived: number;
  }
  interface TopicMapType {
    [key: string]: ITopicStatistic;
  }

  const [rcvTopics, setRcvTopics] = useState<TopicMapType>({});
  const [addrSpaceVersion, _setAddrSpaceVersion] = useState<number>(0);
  const [addrSpaceStore] = useState(() => createPubSubAddressSpaceStore(topicPath));
  const [msgHandlingStore] = useState(() => createPubSubMesageHandlingStore(addrSpaceStore));

  const [reconstructedAddrSpace, setReconstructedAddrSpace] = useState<boolean>(false);

  const callb = useCallback((topic: string, value: Buffer) => {
    setRcvTopics((oldRcvTopics) => {
      const newRcvCouter = oldRcvTopics[topic] === undefined ? 1 : oldRcvTopics[topic].numReceived + 1;

      const updateTopic: TopicMapType = {
        [topic]: {
          lastData: value,
          numReceived: newRcvCouter,
        },
      };
      const newRcvTopics = {
        ...oldRcvTopics,
        ...updateTopic,
      };
      return newRcvTopics;
    });
  }, []);

  const callbData = useCallback(
    (topic: string, value: Buffer) => {
      const pubSubFrame = _parseBuffer<DataFrame>(value);
      if (pubSubFrame === undefined || pubSubFrame.MessageType !== 'ua-data') {
        console.debug('Received invalid frame.', topic, pubSubFrame);
      } else {
        msgHandlingStore.getState().processDataFrame(topic, pubSubFrame);
      }
      callb(topic, value);
    },
    // [callb], //, msgHandlingStore],
    [callb, msgHandlingStore],
  );

  const callbMetadata = useCallback(
    (topic: string, value: Buffer) => {
      const pubSubFrame = _parseBuffer<MetadataFrame>(value);
      if (pubSubFrame === undefined || pubSubFrame.MessageType !== 'ua-metadata') {
        console.debug('Received invalid frame.', topic, pubSubFrame);
      } else {
        msgHandlingStore.getState().processMetadataFrame(topic, pubSubFrame);
        // console.debug('Received metadata frame.', topic);
      }
      if (!addrSpaceStore.getState().isAddressSpaceReconstructed()) {
        const reconstructed = msgHandlingStore.getState().tryAddressSpaceReconstruction();
        setReconstructedAddrSpace(reconstructed);
      }
      callb(topic, value);
    },
    // [callb],
    [addrSpaceStore, callb, msgHandlingStore],
  );

  useMqttV3SubscriptionCallb(PubSubTopics.DataWildcard(topicPath), callbData, 0);
  // DEBUG: delay metadata subscription to expose race condition in address space reconstruction
  useMqttV3SubscriptionCallb(PubSubTopics.MetadataWildcard(topicPath), callbMetadata, 0);

  const mqttTopicStats = Object.keys(rcvTopics).map((i) => {
    const line = `${i}: ${rcvTopics[i].numReceived}`;
    return <p key={i}>{line}</p>;
  });

  return (
    <OpcUaMqttPubSubContext.Provider
      value={{
        addrSpaceStore: addrSpaceStore,
        msgHandlingStore: msgHandlingStore,
      }}
    >
      <OpcUaMqttPubSubContextVersion.Provider value={addrSpaceVersion}>
        <div>
          {showPubSubDebug && (
            <>
              <MqttStatus />
              <hr />
            </>
          )}
          {children}
          {showPubSubDebug && (
            <>
              <hr />

              <hr />
              <pre>
                Reconstructed Address Space: {reconstructedAddrSpace ? 'True' : 'False'}
                <br />
                Number of Nodes: {addrSpaceStore?.getState().numberOfNodes()}
                <br />
                Address Space Version: {addrSpaceVersion} <br />
              </pre>
              <hr />
              <pre>{log.join('\n')}</pre>
              {mqttTopicStats}
            </>
          )}
        </div>
      </OpcUaMqttPubSubContextVersion.Provider>
    </OpcUaMqttPubSubContext.Provider>
  );
}
